import {
  ADD_ACCESS_GROUP_FLOW_TITLE,
  ACCESS_GROUP_CREATION_TIMING,
  ACCESS_GROUP_PROGRESS_RAIL_STEPS,
} from '@/config/addAccessGroupFlow';
import { staticDefaults } from '@/config/static/catalog';
import type { UserRole } from '@/types/common';
import { STORAGE_KEYS, WORKFLOW_QUERY } from '@/utils/constants';
import type {
  AccessGroupCreationSuccess,
  AccessGroupDraft,
  AccessGroupFormStepId,
} from '@/types/accessGroupCreation';
import {
  buildAccessGroupCreationNavigateUrl,
  createLocalAccessGroupCreationConversationId,
  isAccessGroupCreationConversation,
  registerAccessGroupCreationConversation,
} from '@/utils/conversationWorkflow';
import { hasParsedInputPrefill, mergePartialDraft } from '@/utils/draftPrefill';
import { FLOW_CONFIRM_APPROVE_MESSAGE } from '@/utils/flowConfirmCta';
import type { GuidedFlowRuntime } from './types';

const FORM_STEP_ORDER: AccessGroupFormStepId[] = ['details'];
const LOADING_PHASES = ['thinking', 'working', 'almost-ready', 'preview'] as const;

function cloneAccessGroupDefaultDraft(): AccessGroupDraft {
  return {
    ...staticDefaults.accessGroup.defaultDraft,
    selectedReaders: [...staticDefaults.accessGroup.defaultDraft.selectedReaders],
  };
}

function demoAiFilledFieldsForRole(userRole: UserRole): string[] {
  return userRole === 'operator'
    ? [...staticDefaults.accessGroup.operatorAiFilledFields]
    : ['name', 'schedule'];
}

function emptyDraft(): AccessGroupDraft {
  return {
    name: '',
    schedule: '',
    selectedReaders: [],
  };
}

function resolveAccessGroupFormDraft(
  partial: Partial<AccessGroupDraft>,
  userRole: UserRole
): AccessGroupDraft {
  const defaults = userRole === 'operator' ? cloneAccessGroupDefaultDraft() : emptyDraft();
  const merged = mergePartialDraft(defaults, partial);
  return {
    ...merged,
    schedule: merged.schedule || staticDefaults.accessGroup.defaultDraft.schedule,
    selectedReaders: [...merged.selectedReaders],
  };
}

export const accessGroupCreationRuntime: GuidedFlowRuntime<
  AccessGroupDraft,
  AccessGroupFormStepId,
  AccessGroupCreationSuccess
> = {
  id: 'access-group-creation',
  storageKeyPrefix: STORAGE_KEYS.ACCESS_GROUP_CREATION_STATE,
  workflowQuery: WORKFLOW_QUERY.ACCESS_GROUP_CREATION,
  flowTitle: ADD_ACCESS_GROUP_FLOW_TITLE,
  progressRailSteps: ACCESS_GROUP_PROGRESS_RAIL_STEPS,
  timing: ACCESS_GROUP_CREATION_TIMING,
  loadingPhases: LOADING_PHASES,
  formStepOrder: FORM_STEP_ORDER,
  initialFormStep: 'details',
  explainabilityText: staticDefaults.accessGroup.explainability,
  formMessageAssistantHint: staticDefaults.accessGroup.assistantHint,
  idleHint: 'Describe the access group in the box below, then click the arrow to send.',
  savingTitle: 'Saving your access group',
  loadingMessage: 'Preparing access group setup…',
  start: {
    conversationTitle: 'New access group onboarding',
    defaultStarterMessage:
      'Create a first-floor access group for lab reader 1, entrance reader 1, and server room',
    clearStoredState: (conversationId) => {
      localStorage.removeItem(`${STORAGE_KEYS.ACCESS_GROUP_CREATION_STATE}:${conversationId}`);
    },
    registerConversation: registerAccessGroupCreationConversation,
    createLocalConversationId: createLocalAccessGroupCreationConversationId,
    buildNavigateUrl: buildAccessGroupCreationNavigateUrl,
    isConversationRegistered: isAccessGroupCreationConversation,
  },
  emptyDraft,
  createIdleState: (conversationId, userRole) => ({
    conversationId,
    flowId: 'access-group-creation',
    workflowStatus: 'draft',
    uiPhase: 'idle',
    statusLabel: '',
    progressRail: ACCESS_GROUP_PROGRESS_RAIL_STEPS.map((s) => ({
      id: s.id,
      label: s.label,
      status: 'pending' as const,
    })),
    formStep: 'details',
    draft: emptyDraft(),
    userMessages: [],
    assistantHint: '',
    showExplainability: false,
    explainabilityText: staticDefaults.accessGroup.explainability,
    showFormWizard: false,
    showAiBanner: false,
    aiFilledFields: [],
    userRole,
    result: null,
  }),
  hydrateStored: (saved) => {
    const withBanner = { ...saved, showAiBanner: saved.showAiBanner ?? false };
    if (!LOADING_PHASES.includes(withBanner.uiPhase as (typeof LOADING_PHASES)[number])) {
      return withBanner;
    }
    if (withBanner.userMessages.length === 0) {
      return accessGroupCreationRuntime.createIdleState(
        withBanner.conversationId,
        withBanner.userRole
      );
    }
    return {
      ...withBanner,
      uiPhase: 'form',
      formStep: 'details',
      statusLabel: ADD_ACCESS_GROUP_FLOW_TITLE,
      progressRail: saved.progressRail.map((s) => ({ ...s, status: 'completed' as const })),
      workflowStatus: 'awaiting-approval',
      showFormWizard: true,
      introAssistantHint:
        saved.introAssistantHint ??
        (saved.userRole === 'operator'
          ? staticDefaults.accessGroup.operatorAssistantHint
          : staticDefaults.accessGroup.assistantHint.replace('First floor', 'this site')),
      draft: resolveAccessGroupFormDraft(
        saved.draft.name || hasParsedInputPrefill(saved.aiFilledFields)
          ? saved.draft
          : saved.userRole === 'operator'
            ? cloneAccessGroupDefaultDraft()
            : emptyDraft(),
        saved.userRole === 'operator' ? 'operator' : 'novice'
      ),
      aiFilledFields:
        saved.aiFilledFields ??
        demoAiFilledFieldsForRole(saved.userRole === 'operator' ? 'operator' : 'novice'),
      showAiBanner:
        saved.showAiBanner ?? hasParsedInputPrefill(saved.aiFilledFields),
      assistantHint: hasParsedInputPrefill(saved.aiFilledFields)
        ? staticDefaults.accessGroup.operatorAssistantHint
        : saved.userRole === 'operator'
          ? staticDefaults.accessGroup.operatorAssistantHint
          : staticDefaults.accessGroup.assistantHint.replace('First floor', 'this site'),
    };
  },
  buildFormReveal: ({ base, siteLabel, userRole }) => {
    const hasParsedPrefill = hasParsedInputPrefill(base.aiFilledFields);
    const formDraft = resolveAccessGroupFormDraft(base.draft, userRole);
    const aiFields = hasParsedPrefill ? base.aiFilledFields : demoAiFilledFieldsForRole(userRole);
    const hint =
      hasParsedPrefill || userRole === 'operator'
        ? staticDefaults.accessGroup.operatorAssistantHint
        : staticDefaults.accessGroup.assistantHint.replace('First floor', siteLabel);
    return {
      uiPhase: 'form',
      formStep: 'details',
      statusLabel: ADD_ACCESS_GROUP_FLOW_TITLE,
      workflowStatus: 'awaiting-approval',
      showFormWizard: true,
      showExplainability: false,
      showAiBanner: hasParsedPrefill && aiFields.length > 0,
      assistantHint: hint,
      introAssistantHint: hint,
      draft: formDraft,
      aiFilledFields: aiFields,
      userRole,
    };
  },
  isConfirmReady: (draft) =>
    Boolean(draft.name) && Boolean(draft.schedule) && draft.selectedReaders.length > 0,
  isStepNextDisabled: (stepId, draft) => {
    if (stepId === 'details') {
      return !draft.name || !draft.schedule || draft.selectedReaders.length === 0;
    }
    return false;
  },
  getAiFilledFields: (_draft, userRole) => demoAiFilledFieldsForRole(userRole),
  shouldShowExplainability: () => true,
  buildApproveUserMessage: () => FLOW_CONFIRM_APPROVE_MESSAGE,
  buildSuccess: (draft) => {
    const doorCount = draft.selectedReaders.length || 3;
    return {
      result: {
        groupName: draft.name || staticDefaults.accessGroup.success.groupName,
        summaryLine: `${draft.name || staticDefaults.accessGroup.success.groupName} · ${doorCount} doors · ${draft.schedule || 'Always (24/7)'}`,
        nextSteps: [...staticDefaults.accessGroup.success.nextSteps],
      },
      assistantHint:
        'Access group 1 is now in your system. Here’s a quick summary and what you can do next.',
    };
  },
};
